import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import axios from "axios";
import { useTranslation } from "react-i18next";

// Skeleton loader
const BannerSkeleton = () => (
  <div className="relative w-full h-[200px] sm:h-[500px] bg-gray-200 animate-pulse rounded-lg">
    <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse" />
  </div>
);

const Banner = () => {
  const [banners, setBanners] = useState([]);
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(true);
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language || "en";

  // Helper function to get localized text
  const getLocalizedText = (item, field) => {
    if (!item || !item[field]) return "";
    
    // If value is a string (backward compatibility)
    if (typeof item[field] === 'string') {
      return item[field];
    }
    
    // If value is an object with language keys
    if (typeof item[field] === 'object') {
      // Return the value in current language, fallback to English, then first available
      return item[field][currentLanguage] || 
             item[field].en || 
             item[field].hi || 
             item[field].es || 
             item[field].zh || 
             "";
    }
    
    return "";
  };

  const fetchBanners = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/banners/all-banners`);
      if (res?.data && Array.isArray(res.data)) {
        setBanners(res.data);
      } else if (res?.data?.data && Array.isArray(res.data.data)) {
        setBanners(res.data.data);
      } else {
        console.error("Unexpected banner data format:", res.data);
      }
    } catch (err) {
      console.error("Error fetching banners:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  // Update when language changes
  useEffect(() => {
    if (banners.length > 0) {
      // Force re-render to update localized text
      setBanners(prev => [...prev]);
    }
  }, [currentLanguage]);

  // Auto-slide
  useEffect(() => {
    if (banners.length === 0) return;
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % banners.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [banners.length]);

  const nextSlide = () => setCurrent((prev) => (prev + 1) % banners.length);
  const prevSlide = () => setCurrent((prev) => (prev - 1 + banners.length) % banners.length);

  if (loading) return <BannerSkeleton />;

  if (!banners.length)
    return (
      <div className="w-full h-[200px] sm:h-[500px] flex items-center justify-center bg-gray-100 text-gray-500 rounded-lg">
        No banners available
      </div>
    );

  const currentBanner = banners[current];
  const bannerTitle = getLocalizedText(currentBanner, "title");
  const bannerSubTitle = getLocalizedText(currentBanner, "subTitle");

  return (
    <div className="relative w-full h-[250px] sm:h-[500px] overflow-hidden">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentBanner._id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
          className="absolute inset-0 w-full h-full"
        >
          <img
            src={currentBanner.imgUrl}
            alt={bannerTitle}
            className="w-full h-full object-cover"
          />
          {/* Overlay */}
          <div className="absolute inset-0 bg-black/40 flex flex-col justify-center items-center text-center px-4">
            {bannerTitle && (
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.8 }}
                className="text-3xl sm:text-4xl md:text-6xl font-philosopher text-[#FACC15] drop-shadow-lg"
              >
                {bannerTitle}
              </motion.h1>
            )}
            {bannerSubTitle && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="max-w-3xl text-md sm:text-lg md:text-xl mt-2 text-white font-poppins drop-shadow-md text-center"
              >
                {bannerSubTitle}
              </motion.p>
            )}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation Arrows */}
      <div className="absolute inset-0 flex justify-between items-center px-4 md:px-8">
        <button
          onClick={prevSlide}
          className="bg-black/30 hover:bg-black/60 text-white p-2 rounded-full transition"
        >
          <IoIosArrowBack size={28} />
        </button>
        <button
          onClick={nextSlide}
          className="bg-black/30 hover:bg-black/60 text-white p-2 rounded-full transition"
        >
          <IoIosArrowForward size={28} />
        </button>
      </div>

      {/* Modern Dots Indicator */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-3">
        {banners.map((_, index) => (
          <motion.div
            key={index}
            onClick={() => setCurrent(index)}
            className="cursor-pointer rounded-full"
            initial={false}
            animate={{
              width: current === index ? 20 : 10,
              height: current === index ? 10 : 10,
              backgroundColor: current === index ? "#FACC15" : "#9CA3AF",
            }}
            transition={{ duration: 0.3 }}
          />
        ))}
      </div>
    </div>
  );
};

export default Banner;